import { HttpClient, HttpContext } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { EmployeeModel, EmployeeSummaryModel, GangMembershipValidationFactorHistoryModel, GroupModel, PersonAccessModel, PersonAdditionalInformationHistoryModel, PersonAdditionalInformationModel, PersonAndAddressHistoryModel, PersonContactInformationHistoryModel, PersonContactInformationModel, PersonContactInformationsModel, PersonDistinguishingCharacteristicHistoryModel, PersonDistinguishingCharacteristicsModel, PersonEntityType, PersonGangHistoryModel, PersonGangInformationModel, PersonIdentifierHistoryModel, PersonLanguageHistoryModel, PersonLanguagesModel, PersonModel, PersonPhysicalAttributeHistoryModel, PersonPhysicalAttributesModel, PersonPrimaryInformationModel, PersonPrimaryInformationSectionHistoryModel, PersonQuickEditModel, PersonsIdentifierModel, PersonSocialMediaAccountHistoryModel, PersonSocialMediaModel, PersonTypeIdentifierModel, TenantEmployeeGroupItemModel, TenantEmployeeGroupOption, TenantEmployeeModel } from "@lib/person/models";
import { FormMode, CodeType, CodeModel, CountType } from "@lib/shared/models";
import { associationDateSort, calculateAgeFromDate } from "@lib/shared/util";
import { Observable, tap, forkJoin, map, catchError, EMPTY, of, switchMap } from "rxjs";
import { BACKGROUND_REQUEST, EnvironmentService, ToastMessageService, CodeService } from "@lib/shared/data-access";
import { AssociationCountModel, CriminalComplaintPersonModel, PersonAndLocationModel, PersonAndPersonAssociationTypeModel, PersonAndPersonType, PersonCommentModel, PersonIncidentModel, PersonOrganizationModel, PersonPersonModel, PropertyPersonModel, VehiclePersonModel } from "@lib/association/models";
import { WarningModel } from "@lib/warning/models";
import { personAdditionalInformationFields, personContactInformationFields, personDistinguishingCharacteristicsFields, personEmployeeFields, personEntityTypeFields, personGangInformationFields, personIdentifierFields, personLanguagesFields, personLocationFields, personModusOperandiFields, personPhysicalAttributesFields, personPrimaryInformationFields, personSocialMediaFields } from "@lib/configurable-field/models";
import { PersonTileModel } from "@lib/tile/models";
import { AuthService } from "@lib/auth/data-access";
import { PersonModusOperandiModel } from "@lib/modus-operandi/models";
import { AboutEntityModel } from "@lib/about/models";
import { WarningService } from "@lib/warning/data-access";
import { ChargingModel } from "@lib/charging/models";
import { OfficerActivityPersonModel } from "@lib/activity-tracking/models";
import { CLAIMS } from "@lib/auth/models";
import { ContactInformationPhoneNumberEntryModel } from "@lib/contact-information/models";
import { DashboardLayoutModel, WidgetModel } from "@pathfinder/dashboard/models";
import { AlarmPermitPersonModel, AlarmResponseModel } from "@lib/alarm/models";

@Injectable({
    providedIn: 'root'
})
export class PersonService {
    //signals
    private refreshSourceSignal = signal(false);
    public refreshComputedSignal = computed(() => { return this.refreshSourceSignal() });

    private loggedInEmployeeSourceSignal = signal<EmployeeModel | undefined>(undefined);
    public loggedInEmployeeComputedSignal = computed<EmployeeModel | undefined>(() => { return this.loggedInEmployeeSourceSignal(); });

    private personIdSourceSignal = signal(0);
    public personIdComputedSignal = computed(() => { return this.personIdSourceSignal() });

    public personMasterReferenceIdComputedSignal = computed(() => { return this.primaryInfoComputedSignal()?.masterReferenceId || null });

    private personPersonsIdSourceSignal = signal<number | null>(null);
    public personPersonsIdComputedSignal = computed(() => { return this.personPersonsIdSourceSignal() });

    private isCreatingLinkedPerson = signal<boolean>(false);
    public isCreatingLinkedPersonComputedSignal = computed(() => { return this.isCreatingLinkedPerson() });

    private wizardModeSourceSignal = signal(true)
    public wizardModeComputedSignal = computed(() => { return this.wizardModeSourceSignal() });

    private formModeSourceSignal = signal(FormMode.Read)
    public formModeComputedSignal = computed(() => { return this.formModeSourceSignal() });

    private ageOfAdulthoodSignal = signal<number | undefined>(undefined);
    public ageOfAdulthoodComputedSignal = computed(() => { return this.ageOfAdulthoodSignal() });

    // associations
    private personIncidentsSourceSignal = signal<PersonIncidentModel[]>([]);
    public personIncidentsComputedSignal = computed(() => { return this.personIncidentsSourceSignal() });

    private personVehiclesSourceSignal = signal<VehiclePersonModel[]>([]);
    public personVehiclesComputedSignal = computed(() => { return this.personVehiclesSourceSignal() });

    private personPersonsSourceSignal = signal<PersonPersonModel[]>([]);
    public personPersonsComputedSignal = computed(() => { return this.personPersonsSourceSignal() });

    private personCommentsSourceSignal = signal<PersonCommentModel[]>([]);
    public personCommentsComputedSignal = computed(() => { return this.personCommentsSourceSignal() });

    private personLocationSourceSignal = signal<PersonAndLocationModel[]>([]);
    public personLocationComputedSignal = computed(() => { return this.personLocationSourceSignal() });

    private personWarningsSourceSignal = signal<WarningModel[]>([]);
    public personWarningsComputedSignal = computed(() => { return this.personWarningsSourceSignal().sort(this.warningService.sortWarnings) });

    private personChargingsSourceSignal = signal<ChargingModel[]>([]);
    public personChargingsComputedSignal = computed(() => { return this.personChargingsSourceSignal() });

    private personOrganizationSourceSignal = signal<PersonOrganizationModel[]>([]);
    public personOrganizationComputedSignal = computed(() => { return this.personOrganizationSourceSignal() });

    private personActivitiesSourceSignal = signal<OfficerActivityPersonModel[]>([]);
    public personActivitiesComputedSignal = computed<OfficerActivityPersonModel[]>(() => { return this.personActivitiesSourceSignal(); });

    private personPropertiesSourceSignal = signal<PropertyPersonModel[]>([]);
    public personPropertiesComputedSignal = computed<PropertyPersonModel[]>(() => { return this.personPropertiesSourceSignal(); });

    private personEmployeeSourceSignal = signal<EmployeeModel | undefined>(undefined);
    public personEmployeeComputedSignal = computed(() => { return this.personEmployeeSourceSignal() });

    private personEntityTypeSourceSignal = signal<PersonTypeIdentifierModel[]>([]);
    public personEntityTypesComputedSignal = computed(() => { return this.personEntityTypeSourceSignal() });

    private primaryInfoSourceSignal = signal<PersonPrimaryInformationModel | undefined>(undefined);
    public primaryInfoComputedSignal = computed<PersonPrimaryInformationModel | undefined>(() => { return this.primaryInfoSourceSignal(); });

    private associationCountsSourceSignal = signal<AssociationCountModel[]>([]);
    public associationCountsComputedSignal = computed<AssociationCountModel[]>(() => { return this.associationCountsSourceSignal(); });

    private editableGroupsSourceSignal = signal<GroupModel[]>([]);
    public editableGroupsComputedSignal = computed(() => { return this.editableGroupsSourceSignal(); });

    private editGroupSourceSignal = signal<GroupModel | null>(null);
    public editGroupComputedSignal = computed(() => { return this.editGroupSourceSignal(); });

    private chargingDocumentsSourceSignal = signal<CriminalComplaintPersonModel[]>([]);
    public chargingDocumentsComputedSignal = computed<CriminalComplaintPersonModel[]>(() => {
        return this.chargingDocumentsSourceSignal();
    });

    private propertyPersonReleaseToIdSourceSignal = signal<number | undefined>(undefined);
    public propertyPersonReleaseToIdComputedSignal = computed(() => { return this.propertyPersonReleaseToIdSourceSignal() });

    private propertyPersonReleaseToNameSourceSignal = signal<string | undefined>(undefined);
    public propertyPersonReleaseToNameComputedSignal = computed(() => { return this.propertyPersonReleaseToNameSourceSignal() });

    private alarmPermitPersonsSourceSignal = signal<AlarmPermitPersonModel[]>([]);
    public alarmPermitPersonsComputedSignal = computed(() => { return this.alarmPermitPersonsSourceSignal() });

    private alarmResponsesSourceSignal = signal<AlarmResponseModel[]>([]);
    public alarmResponsesComputedSignal = computed(() => { return this.alarmResponsesSourceSignal() });

    private httpClient = inject(HttpClient);
    private codesService = inject(CodeService);
    private environmentService = inject(EnvironmentService);
    private toastMessageService = inject(ToastMessageService);
    private warningService = inject(WarningService);
    public authService = inject(AuthService);

    public updateAssociationCountsSourceSignal(countType: CountType, count: number): void {
        this.associationCountsSourceSignal.update(counts => counts.map(c =>
            c.associationCountType === countType ? { ...c, count: c.count + count } : c
        ));
    }

    public setPersonEmployeeSignal(employee: EmployeeModel | undefined) {
        this.personEmployeeSourceSignal.set(employee);
    }

    public createShellPerson(shellPerson?: PersonPrimaryInformationModel): Observable<PersonPrimaryInformationModel> {
        return this.httpClient.post(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/shell`),
            shellPerson || {}
        );
    }

    public deleteRecord(personId: number): any {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}`),
        )
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError("An error occurred, unable to delete record.");
                    return EMPTY;
                })
            );
    }

    setAgeOfAdulthood(age: number) {
        this.ageOfAdulthoodSignal.set(age);
    }

    public getPersonTypeIdentifiers(personId: number): Observable<PersonTypeIdentifierModel[]> {
        return this.httpClient
            .get<PersonTypeIdentifierModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/entity-types`
                )
            ).pipe(
                tap((response) => {
                    this.personEntityTypeSourceSignal.set(response);
                })
            );
    }

    public updatePersonTypeIdentifiers(personId: number, entityTypes: CodeModel[], saveMasterRecord?: boolean): Observable<PersonTypeIdentifierModel[]> {
        return this.httpClient
            .put<PersonTypeIdentifierModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/entity-types`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                entityTypes.map(x => x.id)
            ).pipe(
                tap((response) => {
                    this.personEntityTypeSourceSignal.update(_ => response);
                })
            );
    }

    public getEntityTypeForm() {
        return forkJoin([of(personEntityTypeFields.fields), of(personEntityTypeFields.customFields)]).pipe(
            map(([entityTypeForm, entityTypeCustomFields]) => ({ entityTypeForm, entityTypeCustomFields }))
        );
    }

    public getPersonComments(personId: number): Observable<PersonCommentModel[]> {
        return this.httpClient.get<PersonCommentModel[]>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personId}/comments`)
        ).pipe(
            switchMap((comments) => {
                if (!comments || !comments.length) return of([]);

                const createdByUserIds = comments.map(comment => comment.comment.createdByUserId);
                const updatedByUserIds = comments.map(comment => comment.comment.updatedByUserId);

                const allUserIds = [...createdByUserIds, ...updatedByUserIds];
                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        return comments.map((personComment) => {
                            personComment.comment = {
                                ...personComment.comment,
                                createdByUser: userNames[personComment.comment.createdByUserId!],
                                updatedByUser: userNames[personComment.comment.updatedByUserId!],
                            }
                            return personComment;
                        });
                    }))
            }),
            tap((comments) => {
                for (const personComment of comments) {
                    personComment.comment.createdOn = new Date(personComment.comment.createdOn!);
                    personComment.comment.updatedOn = new Date(personComment.comment.updatedOn!);
                }
                this.personCommentsSourceSignal.set(comments);
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPersonAndCommentType ? { ...c, count: comments.length } : c
                ));
            })
        )
    }

    public getPersonCommentContent(commentId: number): Observable<Blob> {
        return this.httpClient.get<Blob>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${this.personIdComputedSignal()}/comments/${commentId}/content`),
            {
                responseType: 'blob' as 'json'
            }
        );
    }

    public createPersonComment(personComment: PersonCommentModel): Observable<PersonCommentModel> {
        return this.httpClient.post<PersonCommentModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personComment.personId}/comments`),
            personComment
        ).pipe(
            switchMap((personComment) => {
                if (!personComment) return of({} as PersonCommentModel);

                const allUserIds = [personComment.comment.createdByUserId, personComment.comment.updatedByUserId];
                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        personComment.comment = {
                            ...personComment.comment,
                            createdByUser: userNames[personComment.comment.createdByUserId!],
                            updatedByUser: userNames[personComment.comment.updatedByUserId!],
                        }
                        return personComment;
                    }))
            }),
            tap((personComment) => {
                personComment.comment.createdOn = new Date(personComment.comment.createdOn!);
                personComment.comment.updatedOn = new Date(personComment.comment.updatedOn!);
                this.personCommentsSourceSignal.update(comments => [...comments, personComment]);
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPersonAndCommentType ? { ...c, count: c.count + 1 } : c
                ));
            })
        )
    }

    public updatePersonComment(personComment: PersonCommentModel): Observable<PersonCommentModel> {
        return this.httpClient.put<PersonCommentModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personComment.personId}/comments/${personComment.commentId}`),
            personComment
        ).pipe(
            switchMap((personComment) => {
                if (!personComment) return of({} as PersonCommentModel);

                const allUserIds = [personComment.comment.createdByUserId, personComment.comment.updatedByUserId];
                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        personComment.comment = {
                            ...personComment.comment,
                            createdByUser: userNames[personComment.comment.createdByUserId!],
                            updatedByUser: userNames[personComment.comment.updatedByUserId!],
                        }
                        return personComment;
                    }))
            }),
            tap((personComment) => {
                personComment.comment.createdOn = new Date(personComment.comment.createdOn!);
                personComment.comment.updatedOn = new Date(personComment.comment.updatedOn!);
                this.personCommentsSourceSignal.update(comments => comments.map(c => personComment.id === c.id ? personComment : c));
            })
        )
    }

    public deletePersonComment(personId: number, commentId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personId}/comments/${commentId}`)
        ).pipe(
            tap(() => {
                this.personCommentsSourceSignal.update(comments => comments.filter(c => c.comment?.id !== commentId));
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPersonAndCommentType ? { ...c, count: c.count - 1 } : c
                ));
            })
        )
    }

    creatingLinkedPerson(flag: boolean) {
        this.isCreatingLinkedPerson.set(flag);
    }

    setPersonPersonsId(id: number | null) {
        this.personPersonsIdSourceSignal.set(id);
    }

    editPersonId(id: number) {
        if (!this.isCreatingLinkedPersonComputedSignal()) {
            this.personIdSourceSignal.set(id);
        }
    }

    editWizardMode(wizardMode: boolean) {
        this.wizardModeSourceSignal.set(wizardMode);
    }

    editFormMode(formMode: FormMode) {
        this.formModeSourceSignal.set(formMode);
    }

    // primary information
    public setPrimaryInformationForm() {
        return forkJoin([
            of(personPrimaryInformationFields.fields),
            of(personPrimaryInformationFields.customFields)
        ]).pipe(
            map(([primaryInformationForm, primaryInformationCustomFields]) => ({ primaryInformationForm, primaryInformationCustomFields }))
        );
    }

    public getPrimaryInformation(personId: number) {
        return this.httpClient
            .get<PersonPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/primary-info`)
            ).pipe(
                tap((primaryInfo) => {
                    if (!this.isCreatingLinkedPerson()) {
                        this.primaryInfoSourceSignal.set(primaryInfo)
                    }
                }),
                map(primaryInfo => ({
                    ...primaryInfo,
                    dateOfBirth: primaryInfo.dateOnlyDateOfBirth
                        ? this.formatDateWithoutTimeStamp(primaryInfo.dateOnlyDateOfBirth)
                        : null
                }))
            );
    }

    private formatDateWithoutTimeStamp(date: Date): Date {
        // ensure we only keep YYYY-MM-DD and strip time/offset issues
        const isoString = date.toString().split("T")[0]; // "1990-01-10"
        const [year, month, day] = isoString.split("-").map(Number);
        return new Date(year, month - 1, day);
    }

    public updatePrimaryInformation(updatedPrimaryInformation: PersonPrimaryInformationModel, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<PersonPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${updatedPrimaryInformation.id}/primary-info`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedPrimaryInformation
            ).pipe(
                tap((primaryInfo) => {
                    if (!this.isCreatingLinkedPerson()) {
                        this.primaryInfoSourceSignal.update(_ => primaryInfo)
                        this.refreshSourceSignal.update(v => !v);
                    }
                }),
            );
    }

    public updateQuickEdit(personId: number, quickEditData: PersonQuickEditModel, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<any>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/quick-edit`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                quickEditData
            );
    }

    public setAdditionalInformationForm() {
        return forkJoin([
            of(personAdditionalInformationFields.fields),
            of(personAdditionalInformationFields.customFields)
        ]).pipe(
            map(([additionalInformationForm, additionalInformationCustomFields]) => ({ additionalInformationForm, additionalInformationCustomFields }))
        );
    }

    public getAdditionalInformation(personId: number) {
        return this.httpClient
            .get<PersonAdditionalInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/additional-info`)
            );
    }

    public updateAdditionalInformation(updatedAdditionalInformation: PersonAdditionalInformationModel, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<PersonAdditionalInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${updatedAdditionalInformation.id}/additional-info`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedAdditionalInformation
            );
    }

    public getPersonEmployeeByPersonId(personId: number) {
        if (this.authService.hasAnyClaim([CLAIMS.EMPLOYEE_DATA_READ, CLAIMS.EMPLOYEE_DATA_READ_WRITE])) {
            return this.httpClient
                .get<EmployeeModel>(
                    this.environmentService.pathfinderApiUrl.concat(`/api/v1/employees/persons/${personId}`)
                ).pipe(
                    tap((employee: EmployeeModel) => {
                        this.personEmployeeSourceSignal.set(employee);
                    })
                );
        } else {
            this.personEmployeeSourceSignal.set(undefined);
            return EMPTY;
        }

    }

    public getEmployeesByUserIds(ids: number[]): Observable<EmployeeModel[]> {
        if (!ids.length) return of([]);
        return this.httpClient
            .post<EmployeeModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/employees/all`),
                ids
            );
    }

    public savePersonEmployee(employee: EmployeeModel): Observable<EmployeeModel> {
        return this.httpClient
            .put<EmployeeModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/employees/${employee.id}`),
                employee
            );
    }

    public getPersonEmployeeFormFields() {
        return forkJoin([
            of(personEmployeeFields.fields),
            of(personEmployeeFields.customFields)
        ]).pipe(
            map(([employeeForm, employeeCustomFields]) => ({ employeeForm, employeeCustomFields }))
        );
    }

    public getGangMembershipFactors(personId: number): Observable<PersonGangInformationModel[]> {
        return this.httpClient
            .get<PersonGangInformationModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/gang-memberships`)
            )
            .pipe(
                map((memberships) => {
                    for (const membership of memberships) {
                        for (const factor of membership.gangMembershipValidationFactors) {
                            factor.validatedOn = factor.validatedOn ? new Date(factor.validatedOn) : null;
                        }
                    }
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.PersonGangMembershipType ? { ...c, count: memberships.length } : c
                    ))
                    return memberships;
                })
            );
    }

    public setGangInformationForm() {
        return forkJoin([
            of(personGangInformationFields.fields),
            of(personGangInformationFields.customFields)
        ]).pipe(
            map(([gangInformationForm, gangInformationCustomFields]) => ({ gangInformationForm, gangInformationCustomFields }))
        );
    }

    public saveGangInformation(personId: number, personGangMembership: PersonGangInformationModel[], saveMasterRecord?: boolean, editingSnapshot?: boolean): Observable<PersonGangInformationModel[]> {
        return this.httpClient
            .post<PersonGangInformationModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/gang-memberships`,
                    `?updateMasterRecord=${saveMasterRecord}`,
                    `&editingSnapshot=${editingSnapshot}`
                ),
                personGangMembership
            )
            .pipe(
                map((response) => {
                    for (const membership of response) {
                        for (const factor of membership.gangMembershipValidationFactors) {
                            factor.validatedOn = factor.validatedOn ? new Date(factor.validatedOn) : null;
                        }
                    }
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.PersonGangMembershipType ? { ...c, count: response.length } : c
                    ))
                    return response;
                })
            );
    }

    public setContactInformationForm() {
        return forkJoin([
            of(personContactInformationFields.fields),
            of(personContactInformationFields.customFields)
        ]).pipe(
            map(([contactInformationForm, contactInformationCustomFields]) => ({ contactInformationForm, contactInformationCustomFields }))
        )
    }

    public getContactInformation(personId: number) {
        return forkJoin({
            personContactInformations: this.httpClient.get<PersonContactInformationModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/contact-informations`)),
            contactInformationTypes: this.codesService.getCodes<CodeModel[]>(CodeType.CommonContactInformationType)
        }).pipe(
            map(result => {
                const contactInfo: PersonContactInformationsModel = {
                    id: personId,
                    // TO DO: Fix Issue that's in old code base too 
                    phoneNumbers: result.personContactInformations.filter(c => c.contactInformation.contactInformationTypeId ===
                        result.contactInformationTypes.find((t: any) => t.code === "PHONE")?.id),
                    emailAddresses: result.personContactInformations.filter(c => c.contactInformation.contactInformationTypeId ===
                        result.contactInformationTypes.find((t: any) => t.code === "EMAIL")?.id),
                    webAddresses: result.personContactInformations.filter(c => c.contactInformation.contactInformationTypeId ===
                        result.contactInformationTypes.find((t: any) => t.code === "WEB")?.id)
                }
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.PersonContactInformationType ? { ...c, count: result.personContactInformations.length } : c
                ));
                return contactInfo
            }),
        )
    }

    public updateContactInformation(updatedContactInformation: PersonContactInformationsModel, saveMasterRecord?: boolean) {
        return forkJoin({
            personContactInformations: this.httpClient.put<PersonContactInformationModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${updatedContactInformation.id}/contact-informations`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                [
                    ...updatedContactInformation.phoneNumbers ?? [],
                    ...updatedContactInformation.emailAddresses ?? [],
                    ...updatedContactInformation.webAddresses ?? []
                ]),
            contactInformationTypes: this.codesService.getCodes<CodeModel[]>(CodeType.CommonContactInformationType)
        }).pipe(
            map(result => {
                const contactInfo: PersonContactInformationsModel = {
                    id: updatedContactInformation.id,
                    phoneNumbers: result.personContactInformations.filter(c => c.contactInformation.contactInformationTypeId ===
                        result.contactInformationTypes.find((t: any) => t.code === "PHONE")?.id),
                    emailAddresses: result.personContactInformations.filter(c => c.contactInformation.contactInformationTypeId ===
                        result.contactInformationTypes.find((t: any) => t.code === "EMAIL")?.id),
                    webAddresses: result.personContactInformations.filter(c => c.contactInformation.contactInformationTypeId ===
                        result.contactInformationTypes.find((t: any) => t.code === "WEB")?.id)
                }
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.PersonContactInformationType ? { ...c, count: result.personContactInformations.length } : c
                ));
                return contactInfo;
            })
        )
    }

    public setLanguagesForm() {
        return forkJoin([
            of(personLanguagesFields.fields),
            of(personLanguagesFields.customFields)
        ]).pipe(
            map(([languagesForm, languagesCustomFields]) => ({ languagesForm, languagesCustomFields }))

        );
    }

    public getLanguages(personId: number) {
        return this.httpClient
            .get<PersonLanguagesModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/languages`)
            ).pipe(
                tap((result) => {
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.PersonLanguageType ? { ...c, count: result.length } : c
                    ));
                }));
    }

    public updateLanguages(updatedLanguages: PersonLanguagesModel[], personId: number, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<PersonLanguagesModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/languages`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedLanguages
            ).pipe(
                tap((result) => {
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.PersonLanguageType ? { ...c, count: result.length } : c
                    ));
                }));
    }

    public setSocialMediaForm() {
        return forkJoin([
            of(personSocialMediaFields.fields),
            of(personSocialMediaFields.customFields)
        ]).pipe(
            map(([socialMediaForm, socialMediaCustomFields]) => ({ socialMediaForm, socialMediaCustomFields }))

        )
    }

    public getSocialMedia(personId: number) {
        return this.httpClient
            .get<PersonSocialMediaModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/social-media-accounts`)
            ).pipe(
                tap((result) => {
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.SocialMediaAccountType ? { ...c, count: result.length } : c
                    ));
                }));
    }

    public updateSocialMedia(updatedSocialMedia: PersonSocialMediaModel[], personId: number, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<PersonSocialMediaModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/social-media-accounts`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedSocialMedia
            ).pipe(
                tap((result) => {
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.SocialMediaAccountType ? { ...c, count: result.length } : c
                    ));
                }));
    }

    public setPhysicalAttributesForm() {
        return forkJoin([
            of(personPhysicalAttributesFields.fields),
            of(personPhysicalAttributesFields.customFields)
        ]).pipe(
            map(([physicalAttributesForm, aphysicalAttributesCustomFields]) => ({ physicalAttributesForm, aphysicalAttributesCustomFields }))

        )
    }

    public getPhysicalAttributes(personId: number) {
        return forkJoin({
            physicalAttributesInfo: this.httpClient.get<PersonPhysicalAttributesModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/physical-attributes-info`)),
            physicalAttributeCategory: this.codesService.getCodes<CodeModel[]>(CodeType.PersonPhysicalAttributeCategory)
        }).pipe(
            map(result => {
                const physicalAttributesInfo: PersonPhysicalAttributesModel = {
                    ...result.physicalAttributesInfo,
                    hair: result.physicalAttributesInfo.personPhysicalAttributes.filter(p => p.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((c: any) => c.code === 'HAIR')?.id),
                    eyes: result.physicalAttributesInfo.personPhysicalAttributes.filter(p => p.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((c: any) => c.code === 'EYES')?.id),
                    facial: result.physicalAttributesInfo.personPhysicalAttributes.filter(p => p.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((c: any) => c.code === 'FACIAL')?.id),
                    physicalDeformities: result.physicalAttributesInfo.personPhysicalAttributes.filter(p => p.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((c: any) => c.code === 'PHYSICALDEFORMITIES')?.id)
                }
                return physicalAttributesInfo;
            })
        )
    }

    public updatePhysicalAttributes(updatedPhysicalAttributes: PersonPhysicalAttributesModel, saveMasterRecord?: boolean) {
        return forkJoin({
            personPhysicalAttributes: this.httpClient.put<PersonPhysicalAttributesModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${updatedPhysicalAttributes.id}/physical-attributes-info`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedPhysicalAttributes
            ),
            physicalAttributeCategory: this.codesService.getCodes<CodeModel[]>(CodeType.PersonPhysicalAttributeCategory)
        }).pipe(
            map(result => {
                const physicalAttribute: PersonPhysicalAttributesModel = {
                    id: updatedPhysicalAttributes.id,
                    hair: result.personPhysicalAttributes.personPhysicalAttributes.filter(c => c.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((t: any) => t.code === "HAIR")?.id),
                    eyes: result.personPhysicalAttributes.personPhysicalAttributes.filter(c => c.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((t: any) => t.code === "EYES")?.id),
                    facial: result.personPhysicalAttributes.personPhysicalAttributes.filter(c => c.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((t: any) => t.code === "FACIAL")?.id),
                    physicalDeformities: result.personPhysicalAttributes.personPhysicalAttributes.filter(c => c.physicalAttributeCategoryId ===
                        result.physicalAttributeCategory.find((t: any) => t.code === "PHYSICALDEFORMITIES")?.id),
                    personPhysicalAttributes: updatedPhysicalAttributes.personPhysicalAttributes,
                    weight: updatedPhysicalAttributes.actualWeight,
                    actualWeight: updatedPhysicalAttributes.actualWeight,
                    weightFrom: updatedPhysicalAttributes.weightFrom,
                    weightTo: updatedPhysicalAttributes.weightTo,
                    height: updatedPhysicalAttributes.actualHeight,
                    actualHeight: updatedPhysicalAttributes.actualHeight,
                    heightFrom: updatedPhysicalAttributes.heightFrom,
                    heightTo: updatedPhysicalAttributes.heightTo,
                    buildId: updatedPhysicalAttributes.buildId,
                    complexionId: updatedPhysicalAttributes.complexionId,
                    handednessId: updatedPhysicalAttributes.handednessId
                }
                return physicalAttribute;
            })
        )
    }

    public setDistinguishingCharacteristicsForm() {
        return forkJoin([
            of(personDistinguishingCharacteristicsFields.fields),
            of(personDistinguishingCharacteristicsFields.customFields)
        ]).pipe(
            map(([distinguishingCharacteristicsForm, distinguishingCharacteristicsCustomFields]) => ({ distinguishingCharacteristicsForm, distinguishingCharacteristicsCustomFields }))
        );
    }

    public getDistinguishingCharacteristics(personId: number) {
        return this.httpClient.get<PersonDistinguishingCharacteristicsModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/physical-features`));

    }

    public updateDistinguishingCharacteristics(updatedDistinguishingCharacteristics: PersonDistinguishingCharacteristicsModel[], personId: number, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<PersonDistinguishingCharacteristicsModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/physical-features`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedDistinguishingCharacteristics
            );
    }

    public setPersonIdentifiersForm() {
        return forkJoin([
            of(personIdentifierFields.fields),
            of(personIdentifierFields.customFields)
        ]).pipe(
            map(([personIdentifiersForm, personIdentifiersCustomFields]) => ({ personIdentifiersForm, personIdentifiersCustomFields }))
        );
    }

    public getPersonIdentifiers(personId: number): Observable<PersonsIdentifierModel[]> {
        return this.httpClient
            .get<PersonsIdentifierModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/identifiers`)
            )
            .pipe(
                tap(result => {
                    this.associationCountsSourceSignal.update(counts =>
                        counts.map(c =>
                            c.associationCountType === CountType.PersonIdentifierType ? { ...c, count: result.length } : c
                        ));
                }));
    }

    public updatePersonIdentifiers(updatedPersonIdentifiers: PersonsIdentifierModel[], personId: number, saveMasterRecord?: boolean): Observable<PersonsIdentifierModel[]> {
        return this.httpClient
            .put<PersonsIdentifierModel[]>(
                this.environmentService.pathfinderApiUrl + `/api/v1/persons/${personId}/identifiers` + (saveMasterRecord ? '?updateMasterRecord=true' : ''),
                updatedPersonIdentifiers
            )
            .pipe(
                tap(result => {
                    this.associationCountsSourceSignal.update(counts =>
                        counts.map(c =>
                            c.associationCountType === CountType.PersonIdentifierType ? { ...c, count: result.length } : c
                        ));
                }));
    }


    public setModusOperandisForm() {
        return forkJoin([
            of(personModusOperandiFields.fields),
            of(personModusOperandiFields.customFields)
        ]).pipe(
            map(([modusOperandisForm, modusOperandisCustomFields]) => ({ modusOperandisForm, modusOperandisCustomFields }))
        );
    }

    public getModusOperandis(personId: number) {
        return this.httpClient
            .get<PersonModusOperandiModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/modus-operandis`)
            ).pipe(
                tap((result) => {
                    const uniqueCollectionSequenceCount = new Set(result.map(mo => mo.collectionSequence)).size;
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.PersonModusOperandiType ? { ...c, count: uniqueCollectionSequenceCount } : c
                    ));
                }));
    }

    public updateModusOperandis(updatedModusOperandis: PersonModusOperandiModel[], personId: number, saveMasterRecord?: boolean) {
        return this.httpClient
            .put<PersonModusOperandiModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/persons/${personId}/modus-operandis`,
                    saveMasterRecord ? '?updateMasterRecord=true' : ''
                ),
                updatedModusOperandis
            ).pipe(
                tap((result) => {
                    const uniqueCollectionSequenceCount = new Set(result.map(mo => mo.collectionSequence)).size;
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.PersonModusOperandiType ? { ...c, count: uniqueCollectionSequenceCount } : c,
                    ));
                }));
    }

    public savePersonIncidents(p: PersonIncidentModel) {
        return this.httpClient
            .post<PersonIncidentModel>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/incident-report-person`, p
            )
            .pipe(
                tap((response: PersonIncidentModel) => {
                    this.personIncidentsSourceSignal.update(personIncidents => [...personIncidents, response]);
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                    ));
                }),
            );
    }

    public updatePersonIncidents(p: PersonIncidentModel) {
        return this.httpClient
            .put<PersonIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/incident-report-person?personAndIncidentReportId=${p.id}`
                ),
                p
            )
            .pipe(
                tap((response: PersonIncidentModel) => {
                    this.personIncidentsSourceSignal.update(personIncidents => personIncidents.map(personIncident => personIncident.id === response.id ? response : personIncident));
                }),
            );
    }
    public getPersonIncidents(personId: number): Observable<PersonIncidentModel[]> {
        return this.httpClient
            .get<PersonIncidentModel[]>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/incident-report-person/person/${personId}`
            )
            .pipe(
                tap((incidents) => {
                    // Populate the signal with the fetched data
                    this.personIncidentsSourceSignal.set(incidents);
                }),
                catchError(() => {
                    // Handle the error by setting an empty array to indicate no data or an error state
                    this.personIncidentsSourceSignal.set([]);
                    // Return an observable with an empty array to continue the observable chain
                    return of([]);
                })
            );
    }

    // //TODO: update delete to delete multiple records in a single api call 
    public deletePersonIncidentAssociation(personAndIncidentReportId: number) {
        return this.httpClient.delete(
            `${this.environmentService.pathfinderApiUrl}/api/v1/association/incident-report-person?personAndIncidentReportId=${personAndIncidentReportId}`
        )
            .pipe(
                tap(() => {
                    // On successful delete, update the signal source by removing the deleted item
                    this.personIncidentsSourceSignal.update(personIncidents => personIncidents.filter(personIncident => personIncident.id !== personAndIncidentReportId));
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndIncidentReportType ? { ...c, count: c.count - 1 } : c
                    ));
                }),
            );
    }

    public savePersonVehicle(v: VehiclePersonModel) {
        return this.httpClient
            .post<VehiclePersonModel>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/vehicle-person`, v
            )
            .pipe(
                tap((response) => {
                    this.personVehiclesSourceSignal.update(personVehicles => [...personVehicles, response]);
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAndPersonType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updatePersonVehicle(v: VehiclePersonModel) {
        return this.httpClient
            .put<VehiclePersonModel>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/vehicle-person?personAndVehicleId=${v.id}`, v
            )
            .pipe(
                tap((response: VehiclePersonModel) => {
                    this.personVehiclesSourceSignal.update(personVehicles => personVehicles.map(personVehicle => personVehicle.id === response.id ? response : personVehicle));

                })
            );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deletePersonVehicle(id: number) {
        return this.httpClient
            .delete(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/vehicle-person?personAndVehicleId=${id}`
            )
            .pipe(
                tap(() => {
                    this.personVehiclesSourceSignal.update(personVehicles => personVehicles.filter(personVehicle => personVehicle.id !== id));
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAndPersonType ? { ...c, count: c.count - 1 } : c
                    ));
                })
            );
    }

    public getPersonVehicle(id: number) {
        return this.httpClient
            .get<VehiclePersonModel[]>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/vehicle-person?personId=${id}`
            )
            .pipe(
                tap((vehicles) => {
                    this.personVehiclesSourceSignal.set(vehicles);
                    // this.personVehiclesSubject.next(vehicles);
                }),
                catchError(() => {
                    // Optionally handle the error by setting an empty array or a specific error state
                    this.personVehiclesSourceSignal.set([]);
                    // Depending on your error handling strategy, you might want to throw an error or return a default value
                    return of([]); // Return an observable with an empty array or specific error value
                })
            );
    }

    public savePersonPerson(p: PersonPersonModel) {
        const model = { ...p, person1Id: p.person1Id, person2Id: p.person2Id };
        return this.httpClient
            .post<PersonPersonModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-person`, model)
            .pipe(
                tap((response) => {
                    const newAssociation = this.transformPersonAndPersons(response, this.personIdSourceSignal());
                    this.personPersonsSourceSignal.update(items => [...items, newAssociation]);
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndPersonType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updatePersonPerson(p: PersonPersonModel) {
        const personPersonDto = {
            ...p,
            person1Id: p.isPerson1Master ? p.person1Id : p.person2Id,
            person2Id: p.isPerson1Master ? p.person2Id : p.person1Id,
            person1: p.isPerson1Master ? p.person1 : p.person2,
            person2: p.isPerson1Master ? p.person2 : p.person1,
            isLegalGuardian: p.isPerson1Master ? p.isLegalGuardian : p.isReverseLegalGuardian,
            isReverseLegalGuardian: p.isPerson1Master ? p.isReverseLegalGuardian : p.isLegalGuardian,
        } as PersonPersonModel;

        return this.httpClient
            .put<PersonPersonModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-person?personAndPersonId=${p.id}`, personPersonDto)
            .pipe(
                tap((result) => {
                    this.personPersonsSourceSignal.update(items => [...items.map(assoc => assoc.id === p.id ? { ...assoc, ...p } : assoc)]);
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndPersonType ? { ...c, count: result.length } : c));
                })
            );
    }

    reciprocalTypes: { [key: number]: number | undefined } = {}

    public getLinkedPersons(personId: number) {
        return this.httpClient
            .get<PersonPersonModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-person?person1Id=${personId}`)
            .pipe(
                tap((associations) => {
                    // Transform the fetched associations before updating the BehaviorSubject if needed
                    const transformedResult = associations.map(m => this.transformPersonAndPersons(m, personId));
                    this.personPersonsSourceSignal.set(transformedResult);
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndPersonType ? { ...c, count: associations.length } : c));
                })
            );
    }

    private transformPersonAndPersons(assoc: PersonPersonModel, personId: number): PersonPersonModel {
        const person1IsMaster = personId === assoc.person1Id;
        const hasReverseType = assoc.personAndPersonAssociationTypes?.find(e => e.reverseTypeId);
        if (hasReverseType) {
            this.reciprocalTypes[hasReverseType.typeId] = hasReverseType.reverseTypeId;
            this.reciprocalTypes[hasReverseType.reverseTypeId!] = hasReverseType.typeId;
        }
        return {
            id: assoc.id,
            associatedOn: assoc.associatedOn,
            comment: assoc.comment,
            //if a reverse type exists and the current person is not person1, 
            //we need to reverse these like we do with eveerything else
            personAndPersonAssociationTypes: hasReverseType && !person1IsMaster ?
                assoc.personAndPersonAssociationTypes?.map((m: PersonAndPersonAssociationTypeModel) => {
                    return {
                        id: m.id,
                        typeId: person1IsMaster ? m.typeId : m.reverseTypeId,
                        type: person1IsMaster ? m.personAndPersonType : m.reversePersonType,
                        reverseTypeId: person1IsMaster ? m.reverseTypeId : m.typeId,
                        reversePersonType: person1IsMaster ? m.reversePersonType : m.personAndPersonType,
                    } as PersonAndPersonAssociationTypeModel
                }) :
                assoc.personAndPersonAssociationTypes,
            person1: person1IsMaster ? assoc.person1 : assoc.person2!,
            person1Id: person1IsMaster ? assoc.person1Id : assoc.person2Id!,
            person2: person1IsMaster ? assoc.person2 : assoc.person1,
            person2Id: person1IsMaster ? assoc.person2Id : assoc.person1Id,
            isLegalGuardian: person1IsMaster ? assoc.isLegalGuardian : assoc.isReverseLegalGuardian,
            isReverseLegalGuardian: person1IsMaster ? assoc.isReverseLegalGuardian : assoc.isLegalGuardian,
            isPerson1Master: person1IsMaster
        }
    }

    public getLegalGuardians(personId?: number | null) {
        if (!personId) return of<PersonPersonModel[]>([]);
        return this.httpClient
            .get<PersonPersonModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/persons/${personId}/guardians`)
            .pipe(
                tap((associations: PersonPersonModel[]) => {
                    // Transform the fetched associations before updating the BehaviorSubject if needed
                    associations.map(assoc => {
                        const person1IsMaster = personId === assoc.person1Id;
                        const hasReverseType = assoc.personAndPersonAssociationTypes?.find(e => e.reverseTypeId);
                        if (hasReverseType) {
                            this.reciprocalTypes[hasReverseType.typeId] = hasReverseType.reverseTypeId;
                            this.reciprocalTypes[hasReverseType.reverseTypeId!] = hasReverseType.typeId;
                        }
                        return {
                            id: assoc.id,
                            associatedOn: assoc.associatedOn,
                            comment: assoc.comment,
                            //if a reverse type exists and the current person is not person1, 
                            //we need to reverse these like we do with eveerything else
                            personAndPersonAssociationTypes: hasReverseType && !person1IsMaster ?
                                assoc.personAndPersonAssociationTypes?.map(m => {
                                    return {
                                        id: m.id,
                                        typeId: person1IsMaster ? m.typeId : m.reverseTypeId,
                                        type: person1IsMaster ? m.personAndPersonType : m.reversePersonType,
                                        reverseTypeId: person1IsMaster ? m.reverseTypeId : m.typeId,
                                        reversePersonType: person1IsMaster ? m.reversePersonType : m.personAndPersonType,
                                    }
                                }) :
                                assoc.personAndPersonAssociationTypes,
                            person1: person1IsMaster ? assoc.person1 : assoc.person2,
                            person1Id: person1IsMaster ? assoc.person1Id : assoc.person2Id,
                            person2: person1IsMaster ? assoc.person2 : assoc.person1,
                            person2Id: person1IsMaster ? assoc.person2Id : assoc.person1Id,
                            isLegalGuardian: person1IsMaster ? assoc.isLegalGuardian : assoc.isReverseLegalGuardian,
                            isReverseLegalGuardian: person1IsMaster ? assoc.isReverseLegalGuardian : assoc.isLegalGuardian,
                            isPerson1Master: person1IsMaster
                        } as PersonPersonModel
                    })

                    return associations;
                }));
    }

    public getPersonPersonAssociationTypes(person1Id: number, person2Id: number) {
        return this.httpClient.get<PersonPersonModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/association/person-person/types?person1Id=${person1Id}&person2Id=${person2Id}`
        );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deletePersonPersonAssociation(personAndPersonId: number) {
        return this.httpClient
            .delete(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-person?personAndPersonId=${personAndPersonId}`)
            .pipe(
                tap(() => {
                    this.personPersonsSourceSignal.update(personPersons => personPersons.filter(personPerson => personPerson.id !== personAndPersonId));
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndPersonType ? { ...c, count: c.count - 1 } : c
                    ));
                })
            );
    }


    public getPersonLocations(personId: number): Observable<PersonAndLocationModel[]> {
        return this.httpClient.get<PersonAndLocationModel[]>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/persons/${personId}/addresses`
        ).pipe(
            tap(addresses => {
                this.personLocationSourceSignal.set(addresses);
                this.associationCountsSourceSignal.update(counts =>
                    counts.map(c =>
                        c.associationCountType === CountType.PersonLanguageType
                            ? { ...c, count: addresses.length }
                            : c
                    )
                );
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.PersonAddressType ? { ...c, count: addresses.length } : c
                ));
            })
        );
    }

    public getPersonLocationById(personAndAddressId: number): Observable<PersonAndLocationModel> {
        return this.httpClient.get<PersonAndLocationModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/persons/address/${personAndAddressId}`
        )
    }

    public addPersonAndLocation(personId: number, address: PersonAndLocationModel, saveMasterRecord: boolean) {
        return this.httpClient.post<PersonAndLocationModel>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/persons/${personId}/addresses`,
                saveMasterRecord ? '?updateMasterRecord=true' : ''
            ),
            address
        ).pipe(
            tap(result => {
                this.personLocationSourceSignal.update(personAddress => [...personAddress, result]);
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.PersonAddressType ? { ...c, count: c.count + 1 } : c
                ));
            })
        );
    }

    public updatePersonAndLocation(location: PersonAndLocationModel, saveMasterRecord: boolean) {
        return this.httpClient.put<PersonAndLocationModel>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/persons/address/${location.id}`,
                saveMasterRecord ? '?updateMasterRecord=true' : ''
            ),
            location
        ).pipe(
            tap(updatedLocation => {
                this.personLocationSourceSignal.update(locations => locations.map(l => l.id === updatedLocation.id ? updatedLocation : l));
            })
        );
    }

    public deletePersonAndLocation(personAndAddressId: number, saveMasterRecord: boolean) {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/persons/addresses/${personAndAddressId}`,
                saveMasterRecord ? '?updateMasterRecord=true' : ''
            )
        ).pipe(
            tap(() => {
                this.personLocationSourceSignal.update(personAddress => personAddress.filter(address => address.id !== personAndAddressId));
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.PersonAddressType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setPersonLocationForm() {
        return forkJoin([
            of(personLocationFields.fields),
            of(personLocationFields.customFields)
        ]).pipe(
            map(([personLocationForm, personLocationCustomFields]) => ({ personLocationForm, personLocationCustomFields }))
        );
    }

    getPersonWarnings(personId: number): Observable<WarningModel[]> {
        return this.httpClient.get<WarningModel[]>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personId}/warnings`)
        ).pipe(
            switchMap((warnings) => {
                if (!warnings || !warnings.length) return of([]);

                const createdByUserIds = warnings.map(warning => warning.warning.comment.createdByUserId);
                const updatedByUserIds = warnings.map(warning => warning.warning.comment.updatedByUserId);

                const allUserIds = [...createdByUserIds, ...updatedByUserIds];
                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        return warnings.map((personWarning) => {
                            personWarning.warning.comment = {
                                ...personWarning.warning.comment,
                                createdByUser: userNames[personWarning.warning.comment.createdByUserId!],
                                updatedByUser: userNames[personWarning.warning.comment.updatedByUserId!],
                            }
                            return personWarning;
                        });
                    }))
            }),
            tap((warnings) => {
                for (const personWarning of warnings) {
                    personWarning.warning.comment.createdOn = new Date(personWarning.warning.comment.createdOn!);
                    personWarning.warning.comment.updatedOn = new Date(personWarning.warning.comment.updatedOn!);
                    personWarning.warning.activeFrom = personWarning.warning.activeFrom ? new Date(personWarning.warning.activeFrom) : undefined;
                    personWarning.warning.activeTo = personWarning.warning.activeTo ? new Date(personWarning.warning.activeTo) : undefined;
                }
                this.personWarningsSourceSignal.set(warnings);
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPersonAndWarningType ? { ...c, count: warnings.length } : c
                ));
            })
        )
    }

    public getPersonWarningContent(warningId: number): Observable<Blob> {
        return this.httpClient.get<Blob>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${this.personIdComputedSignal()}/warnings/${warningId}/content`),
            {
                responseType: 'blob' as 'json'
            }
        );
    }

    createPersonWarning(personWarning: WarningModel) {
        return this.httpClient.post<WarningModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personWarning.entityId}/warnings`),
            personWarning
        ).pipe(
            switchMap((personWarning) => {
                if (!personWarning) return of({} as WarningModel);

                const allUserIds = [personWarning.warning.comment.createdByUserId, personWarning.warning.comment.updatedByUserId];
                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        personWarning.warning.comment = {
                            ...personWarning.warning.comment,
                            createdByUser: userNames[personWarning.warning.comment.createdByUserId!],
                            updatedByUser: userNames[personWarning.warning.comment.updatedByUserId!],
                        }
                        return personWarning;
                    }))
            }),
            tap((personWarning) => {
                personWarning.warning.comment.createdOn = new Date(personWarning.warning.comment.createdOn!);
                personWarning.warning.comment.updatedOn = new Date(personWarning.warning.comment.updatedOn!);
                personWarning.warning.activeFrom = personWarning.warning.activeFrom ? new Date(personWarning.warning.activeFrom) : undefined;
                personWarning.warning.activeTo = personWarning.warning.activeTo ? new Date(personWarning.warning.activeTo) : undefined;
                this.personWarningsSourceSignal.update(warnings => [...warnings, personWarning]);
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPersonAndWarningType ? { ...c, count: c.count + 1 } : c
                ));
            })
        )
    }

    updatePersonWarning(personWarning: WarningModel) {
        return this.httpClient.put<WarningModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${personWarning.entityId}/warnings/${personWarning.warningId}`),
            personWarning
        ).pipe(
            switchMap((personWarning) => {
                if (!personWarning) return of({} as WarningModel);

                const allUserIds = [personWarning.warning.comment.createdByUserId, personWarning.warning.comment.updatedByUserId];
                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        personWarning.warning.comment = {
                            ...personWarning.warning.comment,
                            createdByUser: userNames[personWarning.warning.comment.createdByUserId!],
                            updatedByUser: userNames[personWarning.warning.comment.updatedByUserId!],
                        }
                        return personWarning;
                    }))
            }),
            tap((personWarning) => {
                personWarning.warning.comment.createdOn = new Date(personWarning.warning.comment.createdOn!);
                personWarning.warning.comment.updatedOn = new Date(personWarning.warning.comment.updatedOn!);
                personWarning.warning.activeFrom = personWarning.warning.activeFrom ? new Date(personWarning.warning.activeFrom) : undefined;
                personWarning.warning.activeTo = personWarning.warning.activeTo ? new Date(personWarning.warning.activeTo) : undefined;
                this.personWarningsSourceSignal.update(warnings => warnings.map(w => personWarning.id === w.id ? personWarning : w));
            })
        )
    }

    deletePersonWarning(warningId: number) {
        return this.httpClient.delete(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/persons/${this.personIdComputedSignal()}/warnings/${warningId}`)
        ).pipe(
            tap(() => {
                this.personWarningsSourceSignal.update(warnings => warnings.filter(w => w.warningId !== warningId));
                this.associationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPersonAndWarningType ? { ...c, count: c.count - 1 } : c
                ));
            })
        )
    }

    public getPersonChargings(personId: number) {
        return this.httpClient
            .get<ChargingModel[]>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/chargings/persons/${personId}`
            )
            .pipe(
                tap((chargings) => {
                    // Populate the signal with the fetched data
                    this.personChargingsSourceSignal.set(chargings);
                }),
                catchError(() => {
                    // Handle the error by setting an empty array to indicate no data or an error state
                    this.personChargingsSourceSignal.set([]);
                    // Return an observable with an empty array to continue the observable chain
                    return of([]);
                })
            );
    }

    public getAboutInformation(personId: number) {
        return this.httpClient
            .get<AboutEntityModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/about`)
            );
    }

    public getOrganizationAssociations(personId: number) {
        return this.httpClient.get<PersonOrganizationModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-organization?personId=${personId}`)
            .pipe(
                map((result: PersonOrganizationModel[]) => result.map(personOrganizationAssociation => ({
                    ...personOrganizationAssociation,
                    beganOn: !personOrganizationAssociation.beganOn ? undefined : new Date(personOrganizationAssociation.beganOn),
                    endsOn: !personOrganizationAssociation.endsOn ? undefined : new Date(personOrganizationAssociation.endsOn),

                }))),
                tap((result: PersonOrganizationModel[]) => {
                    this.personOrganizationSourceSignal.set(result);
                }),
                catchError(() => {
                    this.personOrganizationSourceSignal.set([]);
                    this.toastMessageService.showError(`An error occurred retrieving organization associations for person with id ${personId}.`);
                    return EMPTY;
                }));
    }

    public createOrganizationAssociation(personOrganization: PersonOrganizationModel): Observable<PersonOrganizationModel> {
        return this.httpClient.post<PersonOrganizationModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-organization?organizationId=${personOrganization.organizationId}&personId=${personOrganization.personId}`, personOrganization)
            .pipe(
                tap((result) => {
                    this.personOrganizationSourceSignal.update(personOrganizations => ([...personOrganizations, result]));
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndOrganizationType ? { ...c, count: c.count + 1 } : c
                    ));
                }),
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred creating a organization association for person with id ${this.personIdComputedSignal()}.`);
                    return EMPTY;
                }));
    }

    public updateOrganizationAssociation(organizationId: number, personId: number, personOrganization: PersonOrganizationModel): Observable<PersonOrganizationModel> {
        return this.httpClient.put<PersonOrganizationModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-organization?organizationId=${organizationId}&personId=${personId}`, personOrganization)
            .pipe(
                tap((result: PersonOrganizationModel) => {
                    this.personOrganizationSourceSignal.update(personOrganizations => personOrganizations.map(personOrganization => personOrganization.id === result.id ? result : personOrganization));
                }),
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred updating a organization association for person with id ${personId}.`);
                    return EMPTY;
                }));
    }

    public deleteOrganizationAssociations(personId: number, personOrganizationIds: number[]): Observable<void> {
        return this.httpClient.delete<void>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/person-organization`, { body: personOrganizationIds })
            .pipe(
                tap(() => {
                    this.personOrganizationSourceSignal.update(personAndOrganization => personAndOrganization.filter(personOrganization => !personOrganizationIds.includes(personOrganization.id!)));
                    this.associationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationOrganizationAndActivityType ? { ...c, count: c.count - personOrganizationIds.length } : c
                    ));
                }),
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred deleting organization associations for person with id ${personId}.`);
                    return EMPTY;
                }));
    }

    public getPersonActivities(personId: number): Observable<OfficerActivityPersonModel[]> {
        return this.httpClient.get<OfficerActivityPersonModel[]>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/association/person-activities/${personId}`
        ).pipe(
            tap(personActivities => {
                this.personActivitiesSourceSignal.set(personActivities);
            })
        );
    }

    public getPersonProperties(personId: number, isOwner: boolean | null = null) {
        let url = `${this.environmentService.pathfinderApiUrl}/api/v1/association/property-person?personId=${personId}`;

        if (isOwner != null) {
            url = `${url}&isOwner=${isOwner}`
        }

        return this.httpClient.get<PropertyPersonModel[]>(url)
            .pipe(
                catchError(() => {
                    this.personPropertiesSourceSignal.set([]);
                    this.toastMessageService.showError(`An error occurred retrieving property associations for person with id ${personId}.`);
                    return EMPTY;
                }),
                tap((response: PropertyPersonModel[]) => {
                    this.personPropertiesSourceSignal.set(response);
                })
            );
    }

    public createSnapshot(personId: number) {
        return this.httpClient.get<{ newPersonId: number }>(`${this.environmentService.pathfinderApiUrl}/api/v1/persons/${personId}/snapshot-person`);
    }

    public getTileInformationById(personId: number) {
        return this.httpClient.get<PersonModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/persons/${personId}/tile-information`);
    }

    getAssociationCounts(personId: number): Observable<AssociationCountModel[]> {
        return this.httpClient.get<AssociationCountModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/persons/${personId}/associations-count`)
            .pipe(
                tap((associationCounts: AssociationCountModel[]) => {
                    this.associationCountsSourceSignal.set(associationCounts);
                }),
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred retrieving association counts for person with id ${personId}.`);
                    this.associationCountsSourceSignal.set([]);
                    return EMPTY;
                }));
    }

    isEmployeeRecord = computed(() => {
        const isEmployeeType = this.personEntityTypesComputedSignal().find(type => type.personTypeIdentifierCodeId == PersonEntityType.AgencyEmployee);
        return isEmployeeType && this.personEmployeeComputedSignal() &&
            (this.authService.hasAnyClaim([
                CLAIMS.CODY_SYSTEMS_EMPLOYEE,
                CLAIMS.ADMINISTRATOR,
                CLAIMS.PERSON_EMPLOYEE_READ,
                CLAIMS.PERSON_EMPLOYEE_READ_WRITE,
                CLAIMS.PERSON_EMPLOYEE_READ_WRITE_DELETE]))
    });
    isAdultRecord = computed(() => {
        const isAdult = this.personPersonsComputedSignal().find(p =>
            p.person1Id === this.personIdComputedSignal() && p.personAndPersonAssociationTypes?.find(ps => ps.typeId === PersonAndPersonType.AdultRecord));

        return (this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ) ||
            this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ_WRITE) ||
            this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ_WRITE_DELETE))
            && isAdult;
    });

    isJuvenileRecord = computed(() => {
        return (this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ) ||
            this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ_WRITE) ||
            this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ_WRITE_DELETE))
            && !!this.personEntityTypesComputedSignal().find(type => type.personTypeIdentifierCodeId == PersonEntityType.Juvenile);
    });

    personHasAgeData = computed(() => {
        const { dateOfBirth, ageFrom, ageTo } = this.primaryInfoComputedSignal()!;
        return dateOfBirth || ageFrom || ageTo;
    });

    canCreateClone() {
        return (this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ_WRITE) ||
            this.authService.hasClaim(CLAIMS.JUVENILE_RECORD_ACCESS_READ_WRITE_DELETE));
    }

    hasClonedRecord = computed(() => {
        return this.personPersonsComputedSignal().find(p =>
            (p.person2Id === this.personIdComputedSignal() || p.person1Id === this.personIdComputedSignal()) &&
            p.personAndPersonAssociationTypes?.find(ps => ps.typeId === PersonAndPersonType.AdultRecord && ps.reverseTypeId === PersonAndPersonType.JuvenileRecord) ||
            p.personAndPersonAssociationTypes?.find(ps => ps.typeId === PersonAndPersonType.JuvenileRecord && ps.reverseTypeId === PersonAndPersonType.AdultRecord)
        );
    })

    clonePersonRecord(personId: number): Observable<PersonModel> {
        return this.httpClient.post<PersonModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/persons/${personId}/clone`, null)
            .pipe(
                tap((clonedPerson: PersonModel) => {
                    if (clonedPerson) {
                        this.associationCountsSourceSignal.update(counts => counts.map(c =>
                            c.associationCountType === CountType.AssociationPersonAndPersonType ? { ...c, count: c.count + 1 } : c
                        ));
                    }
                })
            );
    }

    //#region history
    public getPrimaryInformationHistory(personId: number) {
        return this.httpClient
            .get<PersonPrimaryInformationSectionHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-primary-info-section-history`)
            );
    }

    public getAdditionalInformationHistory(personId: number) {
        return this.httpClient
            .get<PersonAdditionalInformationHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-additional-information-section-history`)
            );
    }

    public getContactInformationHistory(personId: number) {
        return this.httpClient
            .get<PersonContactInformationHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/contact-information-section-history`)
            );
    }

    public getLanguageHistory(personId: number) {
        return this.httpClient
            .get<PersonLanguageHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-language-section-history`)
            );
    }

    public getSocialMediaHistory(personId: number) {
        return this.httpClient
            .get<PersonSocialMediaAccountHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-social-media-account-section-history`)
            );
    }

    public getPhysicalAttributeHistory(personId: number) {
        return this.httpClient
            .get<PersonPhysicalAttributeHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-physical-attribute-section-history`)
            );
    }

    public getDistinguishingCharacteristicsHistory(personId: number) {
        return this.httpClient
            .get<PersonDistinguishingCharacteristicHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-distinguishing-characteristic-section-history`)
            );
    }

    public getPersonIdentifiersHistory(personId: number) {
        return this.httpClient
            .get<PersonIdentifierHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-identifier-section-history`)
            );
    }

    public getPersonGangMembershipHistory(personId: number) {
        return this.httpClient
            .get<PersonGangHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/gang-section-history`)
            );
    }


    public getGangMembershipValidationHistory(personId: number) {
        return this.httpClient
            .get<GangMembershipValidationFactorHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/gang-membership-validation-history`)
            );
    }

    public getPersonAddressHistory(personId: number) {
        return this.httpClient
            .get<PersonAndAddressHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/person-address-section-history`)
            );
    }

    //#endregion

    //#region restrictions
    public restrictPersonAccess(personAccess: PersonAccessModel) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personAccess.personId}/restrict`),
            personAccess
        );
    }

    public getPersonRestrictionAccess(personId: number) {
        return this.httpClient.get<PersonAccessModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/access`), {}
        );
    }

    public removePersonRestriction(personId: number) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/remove-restriction`), {}
        );
    }

    public removeAgencyAndCobraPersonRestriction(personId: number) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/remove-agency-cobra-restrictions`), {}
        );
    }


    public editCobraPersonRestriction(personId: number, isCobraRestricted: boolean) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personId}/cobra-restriction`),
            isCobraRestricted
        );
    }

    public updatePersonAccess(personAccess: PersonAccessModel) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/persons/${personAccess.personId}/update-users-and-groups-access`),
            personAccess
        );
    }

    //#endregion

    public getEmployeeFullName(employee: EmployeeModel): string {
        return this.getFullName(employee.person)
    }

    getInitials(person: PersonModel): string {
        const { firstName, middleName, lastName } = person;
        const getInitial = (name: string) => name.charAt(0).toUpperCase() || '';
        return `${getInitial(firstName || '')}${getInitial(middleName || '')}${getInitial(lastName || '')}`;
    }

    public getFullName(person?: PersonModel): string {
        if (!person) {
            return '';
        }
        if (person.unnamed) {
            return 'Unnamed';
        }

        const nameParts: string[] = [];

        if (person.firstName) {
            nameParts.push(person.firstName);
        }
        if (person.middleName) {
            nameParts.push(person.middleName);
        }
        if (person.lastName) {
            nameParts.push(person.lastName);
        }
        if (person.personNameSuffix?.code) {
            nameParts.push(person.personNameSuffix.code);
        }

        return nameParts.join(' ');
    }

    public getFullNameFromTileSignal(person: PersonTileModel): string {
        let fullName = '';
        if (!person.person?.unnamed && (person.person?.firstName || person.person?.lastName)) {
            fullName = this.getFullName(person.person);
        }
        return fullName.trim() || 'Unnamed';
    }

    public parseStringToNameParts(value: string): { [key: string]: any } {
        const nameParts: { [key: string]: any } = {};
        if (value) {
            if (value.indexOf(',') >= 0) {
                const valueParts = value.split(',');
                valueParts.push(valueParts.shift()!);
                value = valueParts.join(' ');
            }
            if (value.indexOf(' ') < 0) {
                nameParts['lastName'] = value;
            }
            else {
                const valueParts = value.split(/\s+/).filter(s => s);
                if (valueParts.length === 2) {
                    nameParts['firstName'] = valueParts[0].trim();
                    nameParts['lastName'] = valueParts[1].trim();
                }
                else if (valueParts.length > 2) {
                    nameParts['firstName'] = valueParts[0].trim();
                    nameParts['middleName'] = valueParts[1].trim();
                    nameParts['lastName'] = valueParts[2].concat(' ', (valueParts.slice(3) || []).join(' ')).trim();
                }
                else {
                    nameParts['lastName'] = value.trim();
                }
            }

            Object.keys(nameParts).forEach(key => {
                nameParts[key] = nameParts[key].split(' ')
                    .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
                    .join(' ').trim();
            });
        }
        return nameParts;
    }

    public getLinkedPersonDisplayValue(person?: PersonModel): string {
        let fullName = '';
        if (person?.firstName || person?.lastName) {
            fullName = this.getFullName(person);
        }
        fullName = fullName.trim() || 'Unnamed';

        let age = 0;
        if (person?.dateOfBirth) {
            age = calculateAgeFromDate(new Date(person.dateOfBirth));
        }

        return `${fullName} (${person?.sex?.description?.split(' ')[0] || 'Unknown'} | ${person?.race?.description || 'Unknown'} | ${age || 'Unknown'})`;
    }

    getPersonPhoneNumber(phoneNumbers: PersonContactInformationModel[] | undefined): ContactInformationPhoneNumberEntryModel | undefined {
        if (!phoneNumbers || phoneNumbers?.length === 0)
            return;

        // Extract the phone numbers list for readability

        // Filter the phone numbers list to get the primary one
        const primaryPhoneNumber = phoneNumbers?.find(p => p.isPrimary);
        if (primaryPhoneNumber) {
            return { type: primaryPhoneNumber.personAndContactInformationType?.description || null, contactInformation: primaryPhoneNumber.contactInformation };
        }

        // Sort the phone numbers list by associatedOn property
        const sortedPhoneNumbers = phoneNumbers?.sort((a, b) => associationDateSort(a.associatedOn!, b.associatedOn!));

        // Choose the first item if exists, otherwise use an empty array
        const selectedPhoneNumber = primaryPhoneNumber ?? sortedPhoneNumbers?.[0];

        // Determine the phoneValue based on the selectedPhoneNumber
        return { type: selectedPhoneNumber.personAndContactInformationType?.description || null, contactInformation: selectedPhoneNumber.contactInformation };
    }

    //Employee Stuff should be here along with the person stuff
    //In theory Employees don't exist, they just happen to be people who are employed.
    //Without a person there is no employee.
    //signals
    private employeeIdSourceSignal = signal(0);
    public employeeIdComputedSignal = computed(() => { return this.employeeIdSourceSignal() });

    private centralBookingEmployeesSourceSignal = signal<TenantEmployeeGroupOption[]>([]);
    public centralBookingEmployeesComputedSignal = computed(() => { return this.centralBookingEmployeesSourceSignal() })

    getEmployees(): Observable<EmployeeModel[]> {
        return this.httpClient.get<EmployeeModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/employees`)
            .pipe(
                map((employees) =>
                    (employees ?? []).map((e) => ({
                        ...e,
                        disabled: !e.isActive,
                    }))
                ),
            );
    }

    private employeesSourceSignal = signal<EmployeeModel[]>([]);
    public employeesComputedSignal = computed(() => { return this.employeesSourceSignal() })

    getEmployeesForActionCenter(): Observable<EmployeeModel[]> {
        return this.httpClient.get<EmployeeModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/employees`)
            .pipe(
                map((employees) => {
                    const validEmployees = employees?.filter(
                        (employee: EmployeeModel) =>
                            employee.person?.firstName && employee.person?.lastName
                    ) || [];

                    return validEmployees.map((employee: EmployeeModel) => {
                        const name = this.getFullName(employee.person);
                        return {
                            ...employee,
                            fullName: name,
                            badgeNumber: employee.badgeNumber,
                        };
                    });
                }),
                tap((processedEmployees) => {
                    this.employeesSourceSignal.set(processedEmployees);
                })
            )
    }


    getEmployeesThatCanApproveIncidentReviews(): Observable<EmployeeModel[]> {
        return this.httpClient.get<EmployeeModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/employees/incident-reviews/approvers`);
    }

    getEmployeesThatCanCreateIncidentReviews(): Observable<EmployeeModel[]> {
        return this.httpClient.get<EmployeeModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/employees/incident-reviews/creators`);
    }

    getCentralBookingEmployees(): Observable<TenantEmployeeGroupOption[]> {
        return this.httpClient
            .get<TenantEmployeeModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/employees/central-booking`
                )
            )
            .pipe(
                map((employees) =>
                    (employees ?? []).map((e) => ({
                        ...e,
                        disabled: !e.isActive
                    }))
                ),
                map((employees): TenantEmployeeGroupOption[] => {
                    const groups = new Map<number, TenantEmployeeGroupOption>();

                    for (const e of employees) {
                        const tenantId = e.tenantId;
                        const agency = e.agency

                        if (!groups.has(tenantId)) {
                            groups.set(tenantId, {
                                agency,
                                tenantId,
                                employees: []
                            });
                        }

                        const fullName = `${e.firstName}${e.middleName ? ` ${e.middleName}` : ''} ${e.lastName}`.trim();

                        groups.get(tenantId)!.employees.push({
                            fullName,
                            officerBadge: e.officerBadge ?? '',
                            employeeId: e.employeeId,
                            tenantId: e.tenantId,
                            disabled: e.disabled
                        });
                    }

                    const grouped = Array.from(groups.values())
                        .sort((a, b) => a.agency.localeCompare(b.agency));

                    for (const g of grouped) {
                        g.employees.sort((a, b) => {
                            if (!!a.disabled !== !!b.disabled) return a.disabled ? 1 : -1;
                            return a.fullName.localeCompare(b.fullName);
                        });
                    }

                    return grouped;
                }),
                tap((grouped) => {
                    this.centralBookingEmployeesSourceSignal.set(grouped);

                }),
                catchError(() => {
                    this.centralBookingEmployeesSourceSignal.set([]);
                    return of([] as TenantEmployeeGroupOption[]);
                })
            );
    }

    findTenantEmployeeHelper(groups: TenantEmployeeGroupOption[], tenantId?: number | null, employeeId?: number | null): TenantEmployeeGroupItemModel | undefined {
        if (!tenantId || !employeeId) return undefined;
        const group = groups.find((g: TenantEmployeeGroupOption) => g.tenantId === tenantId);
        return group?.employees.find(e => e.employeeId === employeeId);
    }


    // Employee (Officer) Summary records
    public getOfficerSummaries() {
        return this.httpClient
            .get<EmployeeSummaryModel[]>(
                this.environmentService
                    .pathfinderApiUrl
                    .concat(`/api/v1/employees/summary`)
            )
            .pipe(
                map((employees) =>
                    (employees ?? []).map((e) => ({
                        ...e,
                        disabled: !e.isActive,
                    }))
                ),
            );
    }

    public getEmployeeIdForLoggedInUser() {
        return this.httpClient
            .get<EmployeeModel | undefined>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/employees/logged-user`
                )
            ).pipe(
                tap((employee) => {
                    this.loggedInEmployeeSourceSignal.set(employee);
                    if (employee?.id) {
                        this.employeeIdSourceSignal.set(employee.id);
                    }
                }),
                map((employee) => employee?.id)
            )
    }

    public getUniqueNotificationIdentifier(userId: number) {
        //Base64 this value to use as a unique identifier for notifications
        const base64EncodedUserId = btoa(`${userId}-notifications`);
        return `${base64EncodedUserId}`;
    }

    //#region Group
    public getAllGroups() {
        return this.httpClient
            .get<GroupModel[]>(
                this.environmentService
                    .pathfinderApiUrl
                    .concat(`/api/v1/user-group/groups`)
            );
    }

    public getAllGroupsWithUsers() {
        return this.httpClient
            .get<GroupModel[]>(
                this.environmentService
                    .pathfinderApiUrl
                    .concat(`/api/v1/user-group/groups-with-employees`)
            ).pipe(
                map((groups: GroupModel[]) =>
                    groups
                        .map((group) => ({
                            ...group,
                            disabled: group.isExpired ?? false
                        }))
                        .sort((a, b) => (a.isExpired === b.isExpired ? 0 : a.isExpired ? 1 : -1))
                ),
                tap((units) => this.editableGroupsSourceSignal.set(units))
            );
    }

    public updateGroupById(groupId: number, group: GroupModel) {
        return this.httpClient.put<GroupModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/user-group/group/${groupId}`,
            group
        ).pipe(
            tap((result) => {
                this.editableGroupsSourceSignal.update(groups => {
                    const groupExists = groups.some(g => g.id === result.id);
                    if (groupExists) {
                        return groups.map(g => g.id === result.id ? result : g);
                    } else {
                        return [...groups, result];
                    }
                });
            }),
        );
    }
    // signal set
    public setEditGroup(group: GroupModel | null) {
        this.editGroupSourceSignal.set(group);
    }
    //#endregion


    //#region Dashboard
    private dashboardFormModeSourceSignal = signal(FormMode.Read)
    public dashboardFormModeComputedSignal = computed(() => { return this.dashboardFormModeSourceSignal() });

    public editDashboardFormMode(formMode: FormMode) {
        this.dashboardFormModeSourceSignal.set(formMode);
    }

    private selectedLayoutSourceSignal = signal<DashboardLayoutModel | null>(null);
    public selectedLayoutComputedSignal = computed(() => { return this.selectedLayoutSourceSignal() });

    private defaultWidgetsSourceSignal = signal<WidgetModel[]>([]);
    public defaultWidgetsComputedSignal = computed(() => { return this.defaultWidgetsSourceSignal() });

    public setSelectedLayout(layout: DashboardLayoutModel) {
        this.selectedLayoutSourceSignal.set(layout);
    }

    /** @param background a read the app makes on its own (a reconnect reload), not the officer: it must not count as activity for the idle logout. */
    public getDefaultWidgets(background = false) {
        return this.httpClient
            .get<WidgetModel[]>(
                this.environmentService
                    .pathfinderApiUrl
                    .concat(`/api/v1/employees/dashboard/widgets`),
                { context: new HttpContext().set(BACKGROUND_REQUEST, background) }
            )
            .pipe(
                tap((response) => {
                    if (response) {
                        this.defaultWidgetsSourceSignal.set(response);
                    }
                })
            );
    }

    /** @param background as `getDefaultWidgets`. */
    public getEmployeeDashboardLayout(employeeId: number, background = false) {
        return this.httpClient
            .get<DashboardLayoutModel>(
                this.environmentService
                    .pathfinderApiUrl
                    .concat(`/api/v1/employees/${employeeId}/dashboard`),
                { context: new HttpContext().set(BACKGROUND_REQUEST, background) }
            )
            .pipe(
                tap((response) => {
                    if (response) {
                        this.selectedLayoutSourceSignal.set(response);
                    }
                })
            );
    }
    public createEmployeeDashboardLayout(layout: DashboardLayoutModel) {
        return this.httpClient
            .post<DashboardLayoutModel>(
                this.environmentService
                    .pathfinderApiUrl
                    .concat(`/api/v1/employees/dashboard`),
                layout
            )
            .pipe(
                tap((response) => {
                    this.selectedLayoutSourceSignal.set(response);
                })
            );
    }

    public updateEmployeeDashboardLayout(layout: DashboardLayoutModel) {
        return this.httpClient.put<DashboardLayoutModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/employees/${layout.employeeId}/dashboard`, layout)
            .pipe(
                tap((response: DashboardLayoutModel) => {
                    this.selectedLayoutSourceSignal.set(response);
                })
            );
    }

    getChargingDocumentPersons(personId: number): Observable<CriminalComplaintPersonModel[]> {
        return this.httpClient.get<CriminalComplaintPersonModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/charging-document-person?personId=${personId}`))
            .pipe(
                map((response) => {
                    return response.map(r => ({
                        ...r,
                        driverLicenseExpirationDate: r.driverLicenseExpirationDate ? new Date(r.driverLicenseExpirationDate) : undefined
                    }))
                }),
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred retrieving Charging Document person associations.`);
                    return EMPTY;
                }),
                tap((data) => {
                    this.chargingDocumentsSourceSignal.set(data);
                })
            );
    }

    createChargingDocumentPerson(chargingDocumentPerson: CriminalComplaintPersonModel, typeId: number): Observable<CriminalComplaintPersonModel> {
        return this.httpClient.post<CriminalComplaintPersonModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/charging-document-person?typeId=${typeId}`), chargingDocumentPerson)
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred creating Charging Document person associations.`);
                    return EMPTY;
                }),
                tap((data) => {
                    this.chargingDocumentsSourceSignal.update(p => [...p, data]);
                })
            );
    }

    updateChargingDocumentPerson(chargingDocumentPerson: CriminalComplaintPersonModel, typeId: number): Observable<CriminalComplaintPersonModel> {
        return this.httpClient.put<CriminalComplaintPersonModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/charging-document-person/${chargingDocumentPerson.id}?typeId=${typeId}`), chargingDocumentPerson)
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred updating Charging Document person associations.`);
                    return EMPTY;
                }),
                tap((data) => {
                    this.chargingDocumentsSourceSignal.update(p => p.map(wp => wp.id === data.id ? data : wp));
                })
            );
    }

    deleteChargingDocumentPersons(chargingDocumentPersonIds: number[], typeId: number): Observable<void> {
        return this.httpClient.delete<void>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/charging-document-person?personId=${this.personIdComputedSignal()}&typeId=${typeId}`),
            { body: chargingDocumentPersonIds }
        ).pipe(
            catchError(() => {
                this.toastMessageService.showError(`An error occurred deleting the selected person associations.`);
                return EMPTY;
            }),
            tap(() => {
                this.chargingDocumentsSourceSignal.update(sw => sw.filter(s => !chargingDocumentPersonIds.includes(s.id!)));
            })
        );
    }
    //#endregion

    //#region Property Person Release To
    setPropertyPersonReleaseToId(id: number | undefined) {
        this.propertyPersonReleaseToIdSourceSignal.set(id);
    }

    setPropertyPersonReleaseToName(name: string | undefined) {
        this.propertyPersonReleaseToNameSourceSignal.set(name);
    }
    //#endregion 

    public getAlarmPermits(personId: number): Observable<AlarmPermitPersonModel[]> {
        return this.httpClient
            .get<AlarmPermitPersonModel[]>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/alarm-permit-person?personId=${personId}`
            )
            .pipe(
                tap((alarmPermitPerson) => {
                    // Populate the signal with the fetched data
                    this.alarmPermitPersonsSourceSignal.set(alarmPermitPerson);
                }),
                catchError(() => {
                    // Handle the error by setting an empty array to indicate no data or an error state
                    this.alarmPermitPersonsSourceSignal.set([]);
                    // Return an observable with an empty array to continue the observable chain
                    return of([]);
                })
            );
    }

    createAlarmPermitPersons(model: AlarmPermitPersonModel) {
        return this.httpClient.post<AlarmPermitPersonModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/alarm-permit-person`, model)
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred creating an alarm permit association for person with id ${this.personIdComputedSignal()}.`);
                    return EMPTY;
                }),
                tap((response: AlarmPermitPersonModel) => {
                    this.alarmPermitPersonsSourceSignal.update(ap => [...ap, response]);
                }));
    }

    updateAlarmPermitPersons(model: AlarmPermitPersonModel) {
        return this.httpClient.put<AlarmPermitPersonModel>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/alarm-permit-person/${model.id}`, model)
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred creating an alarm permit association for person with id ${this.personIdComputedSignal()}.`);
                    return EMPTY;
                }),
                tap((response: AlarmPermitPersonModel) => {
                    this.alarmPermitPersonsSourceSignal.update(aps => aps.map(ap => ap.id === response.id ? response : ap));
                }));
    }

    deleteAlarmPermitPersons(alarmPermitPersonIds: number[]) {
        return this.httpClient.delete<void>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/alarm-permit-person`, { body: alarmPermitPersonIds })
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred deleting alarm permit associations for person with id ${this.personIdComputedSignal()}.`);
                    return EMPTY;
                }),
                tap(() => {
                    this.alarmPermitPersonsSourceSignal.update(ap => {
                        return ap.filter(ap => !alarmPermitPersonIds.includes(ap.id!));
                    })
                }));
    }


    public getAlarmResponses(personId: number): Observable<AlarmResponseModel[]> {
        return this.httpClient
            .get<AlarmResponseModel[]>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/association/alarm-response-person?personId=${personId}`
            )
            .pipe(
                tap((alarmResponses) => {
                    // Populate the signal with the fetched data
                    this.alarmResponsesSourceSignal.set(alarmResponses);
                }),
                catchError(() => {
                    // Handle the error by setting an empty array to indicate no data or an error state
                    this.alarmResponsesSourceSignal.set([]);
                    // Return an observable with an empty array to continue the observable chain
                    return of([]);
                })
            );
    }
}
